import { defineComponent as _defineComponent } from 'vue'
import { ref, reactive, computed } from 'vue'
import { useReCaptcha } from 'vue-recaptcha-v3'
import { useErrorsGetter, useParsedSetter } from '@/components/Forms/FormMixin'
import { $vfm } from 'vue-final-modal'
import { vacancyResponseCreate, vacancyConfidentialityResource, vacancyTermsofuseResource } from '@/services/vacancy.service'
import { useModalOpener } from '@/composables/useModalOpener'

export default /*#__PURE__*/_defineComponent({
  __name: 'ResponseForm',
  props: {
    close: { type: Function },
    info: {}
  },
  setup(__props: any, { expose: __expose }) {
  __expose();

const props = __props

const isLoad = ref(false)

const formdata = reactive({
  last_name: '',
  first_name: '',
  middle_name: '',
  phone: '',
  email: '',
  attachment1: [] as any[],
  attachment2: [] as any[],
  resume_link: '',
  sourceOption: null as { value: string; label: string } | null,
  cover_letter: '',
  privacy_accepted: false,
  newsletter_accepted: false,
})

const vacancyId = computed(() => props.info?.vacancy?.id ?? props.info?.vacancy?.pk ?? null)

const sourceOptions = [
  { value: 'rabota.ua', label: 'Rabota.ua' },
  { value: 'facebook', label: 'Facebook' },
  { value: 'event', label: 'Івент' },
  { value: 'friends', label: 'Друзі та колеги' },
  { value: 'newsletter', label: 'Розсилка' },
  { value: 'recruiter', label: 'Рекрутер' },
  { value: 'other', label: 'Інше' },
]

const { executeRecaptcha, recaptchaLoaded } = useReCaptcha()

async function parseErrors(response: Response, control: any) {
  try {
    const parsed = await useErrorsGetter(response)
    useParsedSetter(parsed, control.setFieldError)
  } catch {
    const body = await response.json().catch(() => ({}))
    if (body && typeof body === 'object') {
      const state = body.state || body.errors || body
      if (state && typeof state === 'object') {
        Object.keys(state).forEach((key) => {
          const msg = Array.isArray(state[key]) ? state[key][0] : state[key]
          if (msg) control.setFieldError(key, msg)
        })
      }
    }
  }
}

function openConfidentiality() {
  const fallbackMessage = { title: 'Політика конфіденційності', html: '<p>' + (window as any).$t?.('Не вдалося завантажити документ. Спробуйте пізніше.') + '</p>' }
  vacancyConfidentialityResource.execute()
    .then((res: any) => {
      const data = res?.data?.item ?? res?.data ?? res
      useModalOpener({
        message: { title: data?.title || 'Політика конфіденційності', html: data?.content || fallbackMessage.html },
        classes: ['modal--size_lg'],
        component: 'Modals/Vacancy/ContentModal',
      })
    })
    .catch(() => {
      useModalOpener({
        message: fallbackMessage,
        classes: ['modal--size_lg'],
        component: 'Modals/Vacancy/ContentModal',
      })
    })
}

function openTerms() {
  const fallbackMessage = { title: 'Правила та умови', html: '<p>' + (window as any).$t?.('Не вдалося завантажити документ. Спробуйте пізніше.') + '</p>' }
  vacancyTermsofuseResource.execute()
    .then((res: any) => {
      const data = res?.data?.item ?? res?.data ?? res
      useModalOpener({
        message: { title: data?.title || 'Правила та умови', html: data?.content || fallbackMessage.html },
        classes: ['modal--size_lg'],
        component: 'Modals/Vacancy/ContentModal',
      })
    })
    .catch(() => {
      useModalOpener({
        message: fallbackMessage,
        classes: ['modal--size_lg'],
        component: 'Modals/Vacancy/ContentModal',
      })
    })
}

async function send(_sendData: any, control: any) {
  control.setFieldError('nonFieldErrors', '')
  control.setFieldError('privacy_accepted', '')

  if (!formdata.privacy_accepted) {
    control.setFieldError('privacy_accepted', 'Обов\'язкова згода з політикою конфіденційності')
    return
  }

  if (isLoad.value) return
  isLoad.value = true

  try {
    await recaptchaLoaded()
    const captchaToken = await executeRecaptcha()

    const formData = new FormData()
    if (vacancyId.value) formData.append('vacancy', String(vacancyId.value))
    formData.append('last_name', formdata.last_name)
    formData.append('first_name', formdata.first_name)
    formData.append('middle_name', formdata.middle_name)
    formData.append('phone', formdata.phone || '')
    formData.append('email', formdata.email)
    formData.append('resume_link', formdata.resume_link)
    formData.append('source', formdata.sourceOption?.value || '')
    formData.append('cover_letter', formdata.cover_letter)
    formData.append('privacy_accepted', formdata.privacy_accepted ? 'true' : 'false')
    formData.append('newsletter_accepted', formdata.newsletter_accepted ? 'true' : 'false')
    formData.append('captcha', captchaToken)

    const appendFileFromUploader = (fileObj: any, field: string) => {
      if (!fileObj?.data) return
      const dataStr = String(fileObj.data)
      const dataUrlStart = dataStr.indexOf('data:')
      if (dataUrlStart < 0) return
      const dataUrl = dataStr.substring(dataUrlStart)
      const mimeMatch = dataUrl.match(/^data:([^;]+)/)
      const mime = mimeMatch ? mimeMatch[1] : 'application/octet-stream'
      const b64 = dataUrl.includes(',') ? dataUrl.split(',')[1] : ''
      if (!b64) return
      const bstr = atob(b64)
      const u8arr = new Uint8Array(bstr.length)
      for (let i = 0; i < bstr.length; i++) u8arr[i] = bstr.charCodeAt(i)
      const file = new File([u8arr], fileObj.name || 'file', { type: mime })
      formData.append(field, file)
    }
    const files1 = formdata.attachment1?.filter((f: any) => !f.Delete && f.data)
    if (files1?.length) appendFileFromUploader(files1[0], 'attachment_1')
    const files2 = formdata.attachment2?.filter((f: any) => !f.Delete && f.data)
    if (files2?.length) appendFileFromUploader(files2[0], 'attachment_2')

    const response = await vacancyResponseCreate(formData)

    if (response.ok) {
      $vfm.show({ component: 'Vacancies/ThankYouModalContainer' }, {
        classes: ['modal--size_xl'],
      })
      control.resetForm()
      formdata.attachment1 = []
      formdata.attachment2 = []
      formdata.sourceOption = null
      formdata.privacy_accepted = false
      formdata.newsletter_accepted = false
      props.close?.()
    } else {
      await parseErrors(response, control)
    }
  } catch (e: any) {
    if (e && typeof e.json === 'function') {
      await parseErrors(e, control)
    }
  } finally {
    isLoad.value = false
  }
}

const __returned__ = { props, isLoad, formdata, vacancyId, sourceOptions, executeRecaptcha, recaptchaLoaded, parseErrors, openConfidentiality, openTerms, send, ref, reactive, computed, get useReCaptcha() { return useReCaptcha }, get useErrorsGetter() { return useErrorsGetter }, get useParsedSetter() { return useParsedSetter }, get $vfm() { return $vfm }, get vacancyResponseCreate() { return vacancyResponseCreate }, get vacancyConfidentialityResource() { return vacancyConfidentialityResource }, get vacancyTermsofuseResource() { return vacancyTermsofuseResource }, get useModalOpener() { return useModalOpener } }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})